class ImagePreloadService {
  private preloadedImages = new Set<string>();
  private preloadPromises = new Map<string, Promise<void>>();
  private priorityQueue: string[] = [];
  private isProcessing = false;

  // Preload a single image
  async preloadImage(src: string, priority: boolean = false): Promise<void> {
    if (this.preloadedImages.has(src)) {
      return Promise.resolve();
    }

    if (this.preloadPromises.has(src)) {
      return this.preloadPromises.get(src)!;
    }

    const promise = this.performImagePreload(src);
    this.preloadPromises.set(src, promise);

    if (priority) {
      this.priorityQueue.unshift(src);
    } else {
      this.priorityQueue.push(src);
    }

    this.processQueue();
    return promise;
  }

  // Preload multiple images with priority
  async preloadImages(urls: string[], priority: boolean = false): Promise<void> {
    const promises = urls.map(url => this.preloadImage(url, priority));
    await Promise.allSettled(promises);
  }

  // Preload images for a specific companion
  async preloadCompanionImages(companion: any): Promise<void> {
    const imagesToPreload: string[] = [];
    const videosToPreload: string[] = [];

    // Primary photo (highest priority)
    if (companion.assets?.primary_photo) {
      imagesToPreload.push(companion.assets.primary_photo);
    }

    // Primary video
    if (companion.assets?.primary_video) {
      videosToPreload.push(companion.assets.primary_video);
    }

    // Additional photos
    if (companion.assets?.more?.photos) {
      imagesToPreload.push(...companion.assets.more.photos);
    }

    // Additional videos
    if (companion.assets?.more?.videos) {
      videosToPreload.push(...companion.assets.more.videos);
    }

    // Gallery images (legacy)
    if (companion.gallery) {
      imagesToPreload.push(...companion.gallery);
    }

    const totalAssets = imagesToPreload.length + videosToPreload.length;
    if (totalAssets > 0) {
      console.log(`🖼️ Preloading ${imagesToPreload.length} images and ${videosToPreload.length} videos for companion:`, companion.name);
      
      // Preload primary image with high priority
      if (imagesToPreload[0]) {
        await this.preloadImage(imagesToPreload[0], true);
      }

      // Preload remaining images with normal priority
      if (imagesToPreload.length > 1) {
        this.preloadImages(imagesToPreload.slice(1), false);
      }

      // Preload video thumbnails (first frame)
      if (videosToPreload.length > 0) {
        this.preloadVideoThumbnails(videosToPreload);
      }
    }
  }

  // Preload video thumbnails by loading metadata
  private async preloadVideoThumbnails(videoUrls: string[]): Promise<void> {
    const promises = videoUrls.map(url => this.preloadVideoThumbnail(url));
    await Promise.allSettled(promises);
  }

  private async preloadVideoThumbnail(src: string): Promise<void> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      
      video.onloadedmetadata = () => {
        video.currentTime = 0.1; // Seek to first frame
        console.log('✅ Preloaded video thumbnail:', src.split('/').pop());
        resolve();
      };

      video.onerror = () => {
        console.warn('❌ Failed to preload video thumbnail:', src);
        resolve(); // Don't reject, just resolve to continue
      };

      video.src = src;
    });
  }

  // Preload images for multiple companions (e.g., character gallery)
  async preloadCompanionsImages(companions: any[]): Promise<void> {
    console.log(`🖼️ Preloading images for ${companions.length} companions`);
    
    // Process in batches to avoid overwhelming the browser
    const batchSize = 3;
    for (let i = 0; i < companions.length; i += batchSize) {
      const batch = companions.slice(i, i + batchSize);
      await Promise.allSettled(
        batch.map(companion => this.preloadCompanionImages(companion))
      );
      
      // Small delay between batches to prevent blocking
      if (i + batchSize < companions.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  private async performImagePreload(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.add(src);
        this.preloadPromises.delete(src);
        console.log('✅ Preloaded image:', src.split('/').pop());
        resolve();
      };

      img.onerror = () => {
        this.preloadPromises.delete(src);
        console.warn('❌ Failed to preload image:', src);
        reject(new Error(`Failed to preload image: ${src}`));
      };

      img.src = src;
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.priorityQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    // Process up to 3 images concurrently
    const concurrent = 3;
    while (this.priorityQueue.length > 0) {
      const batch = this.priorityQueue.splice(0, concurrent);
      await Promise.allSettled(
        batch.map(src => this.preloadPromises.get(src) || Promise.resolve())
      );
    }

    this.isProcessing = false;
  }

  // Check if image is already preloaded
  isPreloaded(src: string): boolean {
    return this.preloadedImages.has(src);
  }

  // Clear preload cache
  clear(): void {
    this.preloadedImages.clear();
    this.preloadPromises.clear();
    this.priorityQueue = [];
  }

  // Get cache stats
  getStats() {
    return {
      preloadedCount: this.preloadedImages.size,
      pendingCount: this.preloadPromises.size,
      queueLength: this.priorityQueue.length,
    };
  }
}

export const imagePreloadService = new ImagePreloadService();